import classNames from 'classnames';
import {Component} from 'react';
import {connect} from 'react-redux';
import injectSheet from 'react-jss';

import {AppSteps} from './AppData';
import NavigationService from '-/services/NavigationService';
import UIService from '-/services/UIService';
import ApiService from '-/services/ApiService';
import {center, phoneScreen, rem, size, tinyScreen} from '-/styles/utils';
import {get} from '-/util/lodash';

const styles = theme => ({
    root: {
        padding: `0 ${rem(1)}`
    },
    ul: {
        display: 'flex',
        tableLayout: 'fixed',
        position: 'relative',
        margin: '0 auto',
        padding: `${rem(30)} 0`,
        listStyle: 'none',
        whiteSpace: 'nowrap',
        ...tinyScreen({
            padding: `${rem(15)} 0`
        }),
        ...phoneScreen({
            width: '90%'
        }),
        '&::after, &:after': {
            ...center('horizontal'),
            ...size('70%', '1px'),
            content: '""',
            top: rem(43),
            display: 'block',
            backgroundColor: '#d6d6d6',
            zIndex: 1,
            ...tinyScreen({
                top: rem(27)
            })
        }
    },
    li: {
        flex: 1,
        textAlign: 'center',
        '&:first-child': {
            paddingLeft: '0'
        },
        '&:last-child': {
            paddingRight: '0'
        },
        '@media all and (min-width: 320px)': {
            padding: `0 ${rem(2)}`
        },
        '@media all and (min-width: 375px)': {
            padding: `0 ${rem(5)}`
        },
        '@media all and (min-width: 448px)': {
            padding: `0 ${rem(13)}`
        }
    },
    number: {
        display: 'table',
        position: 'relative',
        width: rem(25),
        height: rem(25),
        margin: `0 auto ${rem(10)}`,
        border: '1px solid #d6d6d6',
        borderRadius: '50%',
        backgroundColor: 'white',
        boxShadow: 'inset 0 1px 4px rgba(0, 0, 0, 0.11)',
        zIndex: '2',

        '& span': {
            ...center('both'),
            marginTop: 2,
            display: 'block',
            fontSize: rem(15),
            fontWeight: 'bold',
            lineHeight: rem(20),
            color: '#b1b1b1'
        }
    },
    text: {
        fontSize: rem(11),
        fontWeight: 'normal',
        lineHeight: rem(15),
        textTransform: 'capitalize',
        color: '#979797'
    },
    completed: {
        '& $number': {
            backgroundColor: theme.colors.secondary,
            border: '1px solid white',
            '& span': {
                textIndent: '100%',
                whiteSpace: 'nowrap',
                visibility: 'hidden',
                overflow: 'hidden'
            },
            '&::before, &:before': {
                ...center('both'),
                content: '"\\f00c"',
                display: 'block',
                fontFamily: 'FontAwesome',
                fontSize: rem(14),
                fontWeight: 'normal',
                lineHeight: rem(14),
                color: 'white'
            }
        },
        '& $text': {
            color: 'black'
        }
    },
    active: {
        '& $number': {
            backgroundColor: theme.colors.primary,
            border: '1px solid white',
            '& span': {
                color: 'white'
            }
        },
        '& $text': {
            color: theme.colors.primary
        }
    }
});

class Steps extends Component {
    render() {
        const {
            currentStep,
            stepsCompleted,
            onStepClick,
            isCoApplicant,
            isDMSActive,
            classes = {}
        } = this.props;

        const stepKeys = [
            'buyerInfo',
            'loanInfo',
            'vehicleInfo',
            'employmentInfo'
        ];

        const stepKeysNoShow = [
            'reveal',
            'segment',
            'newOffer',
            'notAccepting',
            'hero',
            'validationInfo',
            'verifyAccountCCR',
            'verifyDob',
            'verifySSN',
            'verifyAccount',
            'verifyAddress',
            'finalReview',
            'finalQuestions',
            'submit',
            'congrats',
            'addCoApp',
            'callback',
            'documents',
            'stips',
            'stipsStatus',
            'stipsWelcome',
            'stipResubmit',
            'stipsSubmitted',
            'stipEdit',
            'pin'
        ];

        if (stepKeysNoShow.indexOf(currentStep) > -1) {
            return null;
        }

        return (
            <section className={classes.root}>
                <ul className={classes.ul}>
                    {stepKeys.filter(step => {
                        const noVehicle = isCoApplicant || isDMSActive;
                        
                        if (step === 'vehicleInfo' && noVehicle) {
                            return false;
                        }

                        return AppSteps.hasOwnProperty(step);
                    }).map((step, i) => {
                        let completed = false;
                        let cls = {};
                        if (step === 'employmentInfo') {
                            completed = stepsCompleted.indexOf('employmentInfo') > 1 && stepsCompleted.indexOf('additionalIncome') > 1;
                            cls = classNames(classes.li, {
                                [classes.active]: currentStep === 'employmentInfo' || currentStep === 'additionalIncome',
                                [classes.completed]: completed
                            });
                        } else {
                            completed = stepsCompleted.indexOf(step) > -1;
                            cls = classNames(classes.li, {
                                [classes.active]: step === currentStep,
                                [classes.completed]: completed
                            });
                        }

                        if (step === 'additionalIncome') return null;

                        let stepTitle = get(AppSteps, `${step}.stepTitle`);

                        if (step === 'buyerInfo' && isCoApplicant) {
                            stepTitle = 'Co-Applicant Info';
                        }

                        return (
                            <li
                                onClick={() => {
                                    if (completed) {
                                        onStepClick(step);
                                    }
                                }}
                                key={i}
                                className={cls}>
                                <div className={classes.number}><span>{i + 1}</span></div>
                                <div className={classes.text}>{stepTitle}</div>
                            </li>
                        );
                    })}
                </ul>
            </section>
        );
    }
}

const mapStateToProps = state => {
    const {currentStep} = state.navigation;
    const stepsCompleted = UIService.getStepsForCurrentApplicant();
    const isDMSActive = get(state, 'creditor.isDMSActive', false);

    return {
        currentStep,
        stepsCompleted,
        isCoApplicant: NavigationService.isCoApplicant(),
        isDMSActive
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onStepClick: step => {
            dispatch(() => {
                NavigationService.next(step);
                ApiService.save('none');
            });
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(injectSheet(styles)(Steps));
